import {
  isKnownVibrationMode,
  KNOWN_VIBRATION_MODES,
  type VibrationMode,
} from '@/types/activity';
import { storage } from '@/utils/storage';

const DISCOVERED_VIBRATION_MODES_KEY = 'discovered_vibration_modes';

const ALL_CAPS_SEGMENT_PATTERN = /^[a-z]{1,2}$/;

export function normalizeVibrationMode(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
}

export function formatVibrationModeLabel(mode: string): string {
  return normalizeVibrationMode(mode)
    .split('-')
    .filter(Boolean)
    .map((segment) => {
      if (ALL_CAPS_SEGMENT_PATTERN.test(segment)) {
        return segment.toUpperCase();
      }

      return segment.charAt(0).toUpperCase() + segment.slice(1);
    })
    .join(' ');
}

export function getPersistedVibrationModes(): VibrationMode[] {
  const stored = storage.get<string[]>(DISCOVERED_VIBRATION_MODES_KEY) ?? [];
  return dedupeVibrationModes([...KNOWN_VIBRATION_MODES, ...stored]);
}

export function persistDiscoveredVibrationModes(modes: readonly string[]): VibrationMode[] {
  const merged = dedupeVibrationModes([...KNOWN_VIBRATION_MODES, ...modes]);
  storage.set(DISCOVERED_VIBRATION_MODES_KEY, merged);
  return merged;
}

export function dedupeVibrationModes(modes: readonly string[]): VibrationMode[] {
  const seen = new Set<string>();
  const normalizedModes: VibrationMode[] = [];

  for (const mode of modes) {
    const normalized = normalizeVibrationMode(mode);
    if (!normalized || seen.has(normalized) || !isKnownVibrationMode(normalized)) {
      continue;
    }
    seen.add(normalized);
    normalizedModes.push(normalized);
  }

  return normalizedModes;
}

export function getVibrationModeGroup(mode: string): 'Metronome' | 'Vibrate' | 'Other' {
  const normalized = normalizeVibrationMode(mode);

  if (normalized.includes('metronome')) {
    return 'Metronome';
  }

  if (normalized.includes('vibrate')) {
    return 'Vibrate';
  }

  return 'Other';
}
